/**
 * Active Order Tracker Bar — Floating real-time tracker at the bottom of the restaurant page.
 * Shows active order status (Received / Preparing / On the way) and allows tapping to track.
 */

import { getCurrentLanguage, t } from '../../../i18n';
import type { RestaurantOrderRecord } from './order-history-sheet';

export function renderActiveOrderTracker(
  orders: RestaurantOrderRecord[],
  hasCartItems: boolean = false,
  tableNumber?: string
): string {
  const activeOrders = (orders || []).filter(
    (o) =>
      o.status === 'pending' ||
      o.status === 'received' ||
      o.status === 'preparing' ||
      o.status === 'cooking' ||
      o.status === 'ready' ||
      o.status === 'on_the_way'
  );

  const bottomOffset = hasCartItems
    ? 'calc(68px + max(env(safe-area-inset-bottom, 0px), 8px))'
    : 'calc(16px + max(env(safe-area-inset-bottom, 0px), 8px))';

  if (activeOrders.length === 0) {
    const titleText = tableNumber
      ? `${t('tableBadge')} ${tableNumber}`
      : t('orderHistoryTitle');

    return `
      <div id="active-order-tracker" onclick="window.viewOrderHistory?.()" style="
        position:fixed;bottom:${bottomOffset};left:12px;right:12px;max-width:480px;margin:0 auto;
        background:#1e293b;color:#fff;border-radius:14px;padding:10px 14px;
        display:flex;align-items:center;justify-content:space-between;gap:10px;
        z-index:190;box-shadow:0 8px 24px rgba(0,0,0,0.22);border:1px solid rgba(255,255,255,0.08);
        cursor:pointer;touch-action:manipulation;transition:bottom 0.25s ease;
      ">
        <div style="display:flex;align-items:center;gap:10px;min-width:0;flex:1;">
          <div style="
            width:34px;height:34px;border-radius:10px;background:rgba(255,255,255,0.1);
            display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0;
          ">
            📋
          </div>
          <div style="min-width:0;flex:1;">
            <div style="display:flex;align-items:center;gap:6px;overflow:hidden;">
              <span style="font-size:12px;font-weight:800;color:#f8fafc;letter-spacing:0.3px;white-space:nowrap;">
                ${titleText}
              </span>
            </div>
            <div style="font-size:11px;color:#94a3b8;margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
              ${t('noOrderHistory')}
            </div>
          </div>
        </div>

        <div style="
          display:inline-flex;align-items:center;gap:4px;background:#334155;color:#f8fafc;
          padding:6px 12px;border-radius:20px;font-size:11px;font-weight:700;flex-shrink:0;
          box-shadow:0 2px 6px rgba(0,0,0,0.2);
        ">
          <span>${t('orderHistoryTitle')}</span>
          <span style="font-size:13px;line-height:1;">›</span>
        </div>
      </div>
    `;
  }

  const lang = getCurrentLanguage();
  const activeOrder = activeOrders[0];

  const outletText = (() => {
    const n = activeOrder.outletName;
    if (!n) return '';
    if (typeof n === 'string') return n.trim();
    return n[lang] || n.en || n.th || '';
  })();

  const statusInfo = (() => {
    switch (activeOrder.status) {
      case 'pending':
      case 'received':
        return {
          icon: '🟡',
          text: t('statusOrderReceived'),
        };
      case 'preparing':
      case 'cooking':
        return {
          icon: '👨‍🍳',
          text: t('activeOrderPreparing'),
        };
      case 'ready':
        return {
          icon: '🛎️',
          text: t('activeOrderReady'),
        };
      case 'on_the_way':
        return {
          icon: '🛵',
          text: t('activeOrderOnTheWay'),
        };
      default:
        return {
          icon: '🍽️',
          text: activeOrder.status,
        };
    }
  })();

  return `
    <div id="active-order-tracker" onclick="window.viewOrderHistory?.()" style="
      position:fixed;bottom:${bottomOffset};left:12px;right:12px;max-width:480px;margin:0 auto;
      background:#0f172a;color:#fff;border-radius:14px;padding:10px 14px;
      display:flex;align-items:center;justify-content:space-between;gap:10px;
      z-index:190;box-shadow:0 8px 24px rgba(0,0,0,0.22);border:1px solid rgba(255,255,255,0.08);
      cursor:pointer;touch-action:manipulation;transition:bottom 0.25s ease;
    ">
      <div style="display:flex;align-items:center;gap:10px;min-width:0;flex:1;">
        <div style="
          width:34px;height:34px;border-radius:10px;background:rgba(255,255,255,0.12);
          display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0;
        ">
          ${statusInfo.icon}
        </div>
        <div style="min-width:0;flex:1;">
          <div style="display:flex;align-items:center;gap:6px;overflow:hidden;">
            <span style="font-size:12px;font-weight:800;color:#f8fafc;letter-spacing:0.3px;white-space:nowrap;">
              ${activeOrder.orderNumber}
            </span>
            ${outletText ? `
              <span style="font-size:11px;color:#94a3b8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                • ${outletText}
              </span>
            ` : ''}
          </div>
          <div style="font-size:11px;color:#cbd5e1;margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
            ${statusInfo.text}
          </div>
        </div>
      </div>

      <div style="
        display:inline-flex;align-items:center;gap:4px;background:#00B900;color:#fff;
        padding:6px 12px;border-radius:20px;font-size:11px;font-weight:700;flex-shrink:0;
        box-shadow:0 2px 6px rgba(0,185,0,0.3);
      ">
        <span>${t('activeOrderTracking')}</span>
        <span style="font-size:13px;line-height:1;">›</span>
      </div>
    </div>
  `;
}
